import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { ProvidersBunchPayload } from "../../gameCenter/providers/slice";
import sanitizeFileName from "helpers/sanitizeFileName";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface ActivationRule {
  currency: string;
  betsMinCount: number;
  depositMinSum: number;
  userCategoryId: number;
  kycConfirmed: number;
  emailConfirmed: number;
  phoneConfirmed: number;
  loyaltyPoints: number;
  rankingPoints: number;
  coins: number;
}
export interface ScoreItem {
  metric: string;
  multiplier: number;
  minValue: number;
  maxValue: number;
  isActive: number;
  weight: number;
}
export interface RewardItem {
  place: string;
  coins: string;
  loyaltyPoints: string;
  rankingPoints: string;
  bonusId: string;
}
export interface TournamentItem {
  id: number;
  description: string;
  title: string;
  currentMembersCount: number;
  maxMembersCount: number;
  botsCount: number;
  image: string | File;
  imageSmall?: string | File;
  portraitImage?: string | File;
  isActive: boolean | number;
  statusId: string;
  startedAt: string;
  finishedAt: string;
  activationRules: ActivationRule[];
  scores: ScoreItem[];
  rewards: RewardItem[];
  contents: TournamentContentItem[];
}

export interface TournamentContentItem {
  locale: string;
  title?: string;
  description?: string;
  content?: string;
  isActive?: number | boolean;
  image?: string | File;
  imageSmall?: string | File;
  portraitImage?: string | File;
}

interface TournamentsState {
  list: TournamentItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  selectedItems: number[];
  payload?: TournamentItem;
  statusList: {
    id: number;
    title: string;
  }[];
  isListStatusFetched: boolean;
  isItemFetching: boolean;
  processingItemId?: TournamentItem["id"];
}
interface GetListPayload {
  limit: number;
  offset: number;
  title: string;
  isActive?: number;
  statusIds: number[];
  currencies: string[];
  order: {
    title?: string;
    currentMembersCount?: string;
    id?: string;
    isActive?: string;
  };
}

export const getTournamentsList = createAsyncThunk(
  "tournaments/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.tournamentsList}`, {
        ...data,
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const createTournament = createAsyncThunk<
  number,
  TournamentItem,
  { rejectValue: ApiErrorPayload }
>("tournaments/create", async (payload, { rejectWithValue }) => {
  const data = new FormData();

  if (payload.image && payload.image instanceof File) {
    const safeName = sanitizeFileName(payload.image.name);
    const safeFile = new File([payload.image], safeName, {
      type: payload.image.type,
    });
    data.append("image", safeFile);
  }
  if (payload.imageSmall && payload.imageSmall instanceof File) {
    const safeName = sanitizeFileName(payload.imageSmall.name);
    const safeFile = new File([payload.imageSmall], safeName, {
      type: payload.imageSmall.type,
    });
    data.append("imageSmall", safeFile);
  }
  if (payload.portraitImage && payload.portraitImage instanceof File) {
    const safeName = sanitizeFileName(payload.portraitImage.name);
    const safeFile = new File([payload.portraitImage], safeName, {
      type: payload.portraitImage.type,
    });
    data.append("portraitImage", safeFile);
  }

  data.append("title", payload.title ?? "");
  data.append("maxMembersCount", payload.maxMembersCount?.toString() ?? "");
  data.append("botsCount", payload.botsCount?.toString() ?? "");
  data.append("description", payload.description ?? "");
  data.append("statusId", payload.statusId ?? "");
  data.append("startedAt", payload.startedAt ?? "");
  data.append("finishedAt", payload.finishedAt ?? "");
  data.append("isActive", payload.isActive ? "1" : "0");

  try {
    const response = await instance.post(apiRoutes.tournamentsCreate, data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data.id;
  } catch (error: any) {
    const apiError = error?.data?.error;
    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const updateTournament = createAsyncThunk<
  boolean,
  TournamentItem,
  { rejectValue: ApiErrorPayload }
>("tournaments/update", async (payload, { rejectWithValue }) => {
  const data = new FormData();

  if (payload.image && payload.image instanceof File) {
    const safeName = sanitizeFileName(payload.image.name);

    const safeFile = new File([payload.image], safeName, {
      type: payload.image.type,
    });

    data.append("image", safeFile);
  }
  if (payload.imageSmall && payload.imageSmall instanceof File) {
    const safeName = sanitizeFileName(payload.imageSmall.name);
    const safeFile = new File([payload.imageSmall], safeName, {
      type: payload.imageSmall.type,
    });
    data.append("imageSmall", safeFile);
  }
  if (payload.portraitImage && payload.portraitImage instanceof File) {
    const safeName = sanitizeFileName(payload.portraitImage.name);
    const safeFile = new File([payload.portraitImage], safeName, {
      type: payload.portraitImage.type,
    });
    data.append("portraitImage", safeFile);
  }
  data.append("title", (payload?.title as string) ?? "");
  data.append(
    "maxMembersCount",
    (payload?.maxMembersCount?.toString() as string) ?? "",
  );
  data.append("description", (payload.description as string) ?? "");
  data.append("statusId", (payload.statusId as string) ?? "");
  data.append("startedAt", (payload.startedAt as string) ?? "");
  data.append("finishedAt", (payload.finishedAt as string) ?? "");
  data.append("isActive", payload.isActive ? "1" : "0");

  try {
    await instance.put(`${apiRoutes.tournamentsUpdate}/${payload.id}`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return true;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError && apiError.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});
export const getTournamentItem = createAsyncThunk(
  "tournaments/read",
  async (id: string | number) => {
    try {
      const result = await instance.get(`${apiRoutes.tournamentsItem}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const getTournamentsStatusList = createAsyncThunk(
  "tournaments/status-list",
  async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.tournamentsStatusesList}?limit=1000&offset=0`,
      );
      return result.data.items;
    } catch (error) {
      return undefined;
    }
  },
);

export const updateTournamentsBunch = createAsyncThunk(
  "game-center/providers/update-bunch",
  async (data: ProvidersBunchPayload) => {
    try {
      await instance.put(`${apiRoutes.tournamentsBunchUpdate}`, {
        ...data,
        isActive: data.isActive,
      });
      return true;
    } catch (error) {
      return false;
    }
  },
);
export const deleteTournamentsItem = createAsyncThunk(
  "tournaments/delete",
  async (id: string | number) => {
    try {
      await instance.delete(`${apiRoutes.tournamentsDelete}/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const setTournamentContent = createAsyncThunk<
  boolean,
  { tournamentId: number; payload: TournamentContentItem },
  { rejectValue: ApiErrorPayload }
>(
  "tournament/content/set",
  async ({ tournamentId, payload }, { rejectWithValue }) => {
    try {
      const data = new FormData();

      data.append("locale", payload.locale ?? "");
      data.append("title", payload.title ?? "");
      data.append("description", payload.description ?? "");
      data.append("content", payload.content ?? "");
      data.append("isActive", payload.isActive ? "1" : "0");

      if (payload.image instanceof File) {
        const safeName = sanitizeFileName(payload.image.name);

        data.append(
          "image",
          new File([payload.image], safeName, { type: payload.image.type }),
        );
      }
      if (payload.imageSmall instanceof File) {
        const safeName = sanitizeFileName(payload.imageSmall.name);

        data.append(
          "imageSmall",
          new File([payload.imageSmall], safeName, {
            type: payload.imageSmall.type,
          }),
        );
      }
      if (payload.portraitImage instanceof File) {
        const safeName = sanitizeFileName(payload.portraitImage.name);

        data.append(
          "portraitImage",
          new File([payload.portraitImage], safeName, {
            type: payload.portraitImage.type,
          }),
        );
      }

      await instance.put(
        `${apiRoutes.tournamentsSetContent}/${tournamentId}`,
        data,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      return true;
    } catch (error: any) {
      const data = error?.data;
      const apiError = data?.error ?? data;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

const initialState: TournamentsState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  statusList: [],
  isListStatusFetched: false,
  isItemFetching: false,
};

export const tournamentsSlice = createSlice({
  name: "tournaments",
  initialState,
  reducers: {
    handlePaginate: (
      state: TournamentsState,
      action: PayloadAction<number>,
    ) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleUpdateFields: (
      state: TournamentsState,
      action: PayloadAction<TournamentItem>,
    ) => {
      state.payload = { ...state.payload, ...action.payload };
    },
    handleClearFields: (state: TournamentsState) => {
      state.payload = undefined;
      state.isItemFetched = false;
    },
    handleSelectItem: (
      state: TournamentsState,
      action: PayloadAction<number>,
    ) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: TournamentsState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id ?? 0);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTournamentsList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(createTournament.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createTournament.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });
    builder.addCase(createTournament.rejected, (state, action) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateTournament.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateTournament.fulfilled, (state, action) => {
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, {
              ...action.meta.arg,
              image:
                action.meta.arg.image instanceof File
                  ? item.image
                  : action.meta.arg.image,
              imageSmall:
                action.meta.arg.imageSmall instanceof File
                  ? item.imageSmall
                  : action.meta.arg.imageSmall,
              portraitImage:
                action.meta.arg.portraitImage instanceof File
                  ? item.portraitImage
                  : action.meta.arg.portraitImage,
            })
          : item,
      );
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updateTournament.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(deleteTournamentsItem.pending, (state, action) => {
      state.processingItemId = +action.meta.arg || undefined;
    });
    builder.addCase(deleteTournamentsItem.fulfilled, (state) => {
      state.isListFetched = false;
      state.processingItemId = undefined;
    });
    builder.addCase(deleteTournamentsItem.rejected, (state) => {
      state.isListFetched = false;
      state.processingItemId = undefined;
    });
    builder.addCase(getTournamentItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      const data = action.payload;
      if (!data) {
        state.payload = undefined;
        return;
      }

      state.payload = {
        ...data,
        image: data.image ?? "",
        imageSmall: data.imageSmall ?? "",
        portraitImage: data.portraitImage ?? "",
        contents: (data.contents ?? []).map((item: TournamentContentItem) => ({
          ...item,
          image: item.image ?? "",
          imageSmall: item.imageSmall ?? "",
          portraitImage: item.portraitImage ?? "",
        })),
      };
    });
    builder.addCase(updateTournamentsBunch.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
    builder.addCase(getTournamentsStatusList.fulfilled, (state, action) => {
      state.isListStatusFetched = true;
      state.statusList = action.payload ?? [];
    });
    builder.addCase(setTournamentContent.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(setTournamentContent.fulfilled, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(setTournamentContent.rejected, (state) => {
      state.isItemFetching = false;
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleUpdateFields,
  handleClearFields,
} = tournamentsSlice.actions;
